import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/adminApi';

const ADMIN_API = `${process.env.REACT_APP_EXPRESS_API_URL || 'https://famelia-admin-api-web.azurewebsites.net'}/api/admin`;

interface SignupEntry {
  id: string;
  name: string;
  email: string;
  referral_code: string;
  dietitian_requested: boolean;
  insurance_plan: string;
  created_at: string;
  onboarding_completed: boolean;
  is_archived: boolean;
}

export function SignupsTab() {
  const [signups, setSignups] = useState<SignupEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [archivingId, setArchivingId] = useState<string | null>(null);

  const authHeader = { Authorization: adminApi.getAuthToken() };

  const fetchSignups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${ADMIN_API}/signups`, { headers: authHeader });
      setSignups(res.data.signups ?? []);
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to load signups.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchSignups(); }, [fetchSignups]);

  const handleToggleArchive = async (s: SignupEntry) => {
    const next = !s.is_archived;
    if (next && !window.confirm(`Archive ${s.name || s.email}? They'll be hidden from people search in the app.`)) return;
    setArchivingId(s.id);
    setError(null);
    try {
      await axios.patch(
        `${ADMIN_API}/signups/${s.id}/archive`,
        { archived: next },
        { headers: authHeader },
      );
      setSignups((prev) => prev.map((row) => (row.id === s.id ? { ...row, is_archived: next } : row)));
    } catch (err: any) {
      setError(err.response?.data?.error ?? err.message ?? 'Failed to update archive status.');
    } finally {
      setArchivingId(null);
    }
  };

  const filtered = signups.filter((s) => {
    if (!showArchived && s.is_archived) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      s.name.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q) ||
      s.referral_code.toLowerCase().includes(q) ||
      s.insurance_plan.toLowerCase().includes(q)
    );
  });

  const referralCount = signups.filter((s) => s.referral_code).length;
  const dietitianCount = signups.filter((s) => s.dietitian_requested).length;
  const insuredCount = signups.filter((s) => s.insurance_plan).length;
  const archivedCount = signups.filter((s) => s.is_archived).length;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Signups & Referrals
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Chip label={`${signups.length} total signups`} color="primary" variant="outlined" />
        <Chip label={`${referralCount} with referral code`} variant="outlined" sx={{ color: '#7b1fa2', borderColor: '#7b1fa2' }} />
        <Chip label={`${dietitianCount} requested dietitian`} color="success" variant="outlined" />
        <Chip label={`${insuredCount} with insurance`} color="info" variant="outlined" />
        <Chip label={`${archivedCount} archived`} variant="outlined" sx={{ color: '#8a7d72', borderColor: '#8a7d72' }} />
      </Stack>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name, email, referral code, or insurance..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={<Switch checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
          label="Show archived"
        />
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Referral Code</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Dietitian Requested</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Insurance</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Signed Up</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'No matching signups found.' : 'No signups yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((s) => (
                  <TableRow key={s.id} hover sx={s.is_archived ? { opacity: 0.55 } : undefined}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {s.name}
                        {s.is_archived && (
                          <Chip label="Archived" size="small" sx={{ ml: 1, height: 18, fontSize: 11 }} />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {s.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {s.referral_code ? (
                        <Chip label={s.referral_code} size="small" variant="outlined" sx={{ color: '#7b1fa2', borderColor: '#7b1fa2' }} />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {s.dietitian_requested ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Yes"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      ) : (
                        <Chip
                          icon={<CancelIcon />}
                          label="No"
                          size="small"
                          variant="outlined"
                          sx={{ color: 'text.disabled', borderColor: 'divider' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {s.insurance_plan ? (
                        <Chip label={s.insurance_plan} size="small" color="info" variant="outlined" />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(s.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        color={s.is_archived ? 'primary' : 'inherit'}
                        disabled={archivingId === s.id}
                        startIcon={s.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                        onClick={() => handleToggleArchive(s)}
                      >
                        {s.is_archived ? 'Unarchive' : 'Archive'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
